import { ALL_FORMATS, BlobSource, Input } from 'mediabunny';
import type { VideoInfo } from '../state/store';

/** Reads the container headers of the picked file. Only the parts it needs are read; the file is never loaded whole. */
export async function probeVideo(file: File): Promise<VideoInfo> {
  const input = new Input({ source: new BlobSource(file), formats: ALL_FORMATS });
  try {
    const video = await input.getPrimaryVideoTrack();
    if (!video) throw new Error('This file has no video track.');
    const audio = await input.getPrimaryAudioTrack();
    const duration = await input.computeDuration();
    let fps: number | null = null;
    try {
      const stats = await video.computePacketStats(120);
      fps = stats.averagePacketRate ? Math.round(stats.averagePacketRate * 100) / 100 : null;
    } catch {
      fps = null;
    }
    return {
      duration,
      width: video.displayWidth,
      height: video.displayHeight,
      fps,
      codec: video.codec,
      hasAudio: !!audio,
      canDecode: await video.canDecode(),
    };
  } finally {
    input.dispose();
  }
}

export function browserSupport(): { ok: boolean; saveToDisk: boolean; reason?: string } {
  const hasCodecs = typeof window !== 'undefined' && 'VideoEncoder' in window && 'VideoDecoder' in window && 'AudioEncoder' in window;
  const saveToDisk = typeof window !== 'undefined' && typeof window.showSaveFilePicker === 'function';
  if (!hasCodecs) return { ok: false, saveToDisk, reason: 'This browser cannot encode video. Use Chrome or Edge on a laptop or desktop.' };
  return { ok: true, saveToDisk };
}
